"use client"

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Send } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';

export default function AdvertiseContactForm() {
  const [formData, setFormData] = useState({
    inquiryType: "Brand Sponsorship",
    fullName: "",
    organization: "",
    email: "",
    website: "",
    expectedBudget: "",
    message: "",
  });

  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [isFormValid, setIsFormValid] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const isValid = formData.fullName.trim().length > 0 && 
                    formData.organization.trim().length > 0 && 
                    formData.email.trim().length > 0 && 
                    formData.message.trim().length > 0;
    setIsFormValid(isValid);
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    if (!turnstileToken) {
      alert("Please wait for security verification to complete.");
      return;
    }

    setStatus("loading");
    setErrorMessage("");
    try {
      const res = await fetch("/api/contact/advertise", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, turnstileToken }),
      });

      const data = await res.json();

      if (res.ok) {
        setStatus("success");
        setTurnstileToken("");
        setFormData({
          inquiryType: "Brand Sponsorship",
          fullName: "",
          organization: "",
          email: "",
          website: "",
          expectedBudget: "",
          message: "",
        });
      } else {
        setStatus("error");
        setErrorMessage(data.message || "Something went wrong.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage("Network error occurred.");
    }
  };

  const inputClasses = "w-full bg-[#0a0a0a] border border-white/10 px-5 py-4 text-sm font-sans outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all text-white placeholder:text-zinc-600";

  return (
    <section id="contact" className="py-24 bg-black text-white relative overflow-hidden border-t border-white/10">
      <style dangerouslySetInnerHTML={{
        __html: `
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus, 
        input:-webkit-autofill:active,
        textarea:-webkit-autofill,
        textarea:-webkit-autofill:hover,
        textarea:-webkit-autofill:focus,
        textarea:-webkit-autofill:active,
        select:-webkit-autofill,
        select:-webkit-autofill:hover,
        select:-webkit-autofill:focus,
        select:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 1000px #0a0a0a inset !important;
            -webkit-text-fill-color: white !important;
            transition: background-color 5000s ease-in-out 0s;
        }
      `}} />
      <div className="absolute inset-0 bg-[url('/noise.png')] opacity-10 mix-blend-overlay pointer-events-none" />
      <div className="container mx-auto px-4 md:px-8 relative z-10">
        
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-5xl font-bold mb-4">Initiate Partnership</h2>
            <p className="text-zinc-400 font-mono text-sm uppercase tracking-widest">Connect with the B2B Desk</p>
          </div>

          <div className="relative">
            {/* Tech Vault Design */}
            <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-3xl border border-white/10 shadow-2xl" 
                 style={{ clipPath: 'polygon(0 0, calc(100% - 30px) 0, 100% 30px, 100% 100%, 30px 100%, 0 calc(100% - 30px))' }} />
            
            {/* Edge Accents */}
            <div className="absolute top-0 right-[30px] w-8 h-px bg-brand" />
            <div className="absolute bottom-0 left-[30px] w-8 h-px bg-brand" />

            <div className="relative p-8 md:p-12 z-10">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center text-center space-y-6 py-20"
                  >
                    <div className="w-20 h-20 bg-brand/10 flex items-center justify-center border border-brand/40" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}>
                      <CheckCircle2 className="w-10 h-10 text-brand" />
                    </div>
                    <h3 className="text-3xl font-serif font-bold text-white">Inquiry Transmitted.</h3>
                    <p className="text-sm text-zinc-400 font-mono max-w-[320px] leading-relaxed">Our B2B division will review your details and initiate contact shortly.</p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    className="space-y-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    {/* Invisible Cloudflare Turnstile */}
                    <div className="w-full flex justify-center hidden">
                      <Turnstile
                        siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                        onSuccess={(token) => setTurnstileToken(token)}
                        options={{ theme: 'dark' }}
                      />
                    </div>

                    {status === "error" && (
                       <div className="bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-3 text-sm flex items-center justify-center font-mono">
                         {errorMessage}
                       </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Inquiry Type</label>
                        <select
                          required
                          className={`${inputClasses} appearance-none cursor-pointer`}
                          value={formData.inquiryType}
                          onChange={(e) => setFormData({ ...formData, inquiryType: e.target.value })}
                          style={{ colorScheme: 'dark' }}
                        >
                          <option value="Premium Formats Sponsorship" style={{ background: '#0a0a0a', color: '#fff' }}>Premium Formats Sponsorship</option>
                          <option value="Vanguard Closed Beta (API & RAG)" style={{ background: '#0a0a0a', color: '#fff' }}>Vanguard Closed Beta (API & RAG)</option>
                          <option value="B2B Enterprise Data Feed" style={{ background: '#0a0a0a', color: '#fff' }}>B2B Enterprise Data Feed</option>
                          <option value="Custom Strategic Partnership" style={{ background: '#0a0a0a', color: '#fff' }}>Custom Strategic Partnership</option>
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Executive Name</label>
                        <input
                          required
                          type="text"
                          className={inputClasses}
                          placeholder="Your Full Name"
                          value={formData.fullName}
                          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Organization</label>
                        <input
                          required
                          type="text"
                          className={inputClasses}
                          placeholder="Company Name"
                          value={formData.organization}
                          onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Corporate Email</label>
                        <input
                          required
                          type="email"
                          className={inputClasses}
                          placeholder="executive@company.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Website URL (Optional)</label>
                        <input
                          type="url"
                          className={inputClasses}
                          placeholder="https://company.com"
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Expected Budget (Optional)</label>
                        <select
                          className={`${inputClasses} appearance-none cursor-pointer`}
                          value={formData.expectedBudget}
                          onChange={(e) => setFormData({ ...formData, expectedBudget: e.target.value })}
                          style={{ colorScheme: 'dark' }}
                        >
                          <option value="" disabled style={{ background: '#0a0a0a', color: '#71717a' }}>Select Range</option>
                          <option value="<$5k / Month" style={{ background: '#0a0a0a', color: '#fff' }}>&lt; $5,000 / Month</option>
                          <option value="$5k - $15k / Month" style={{ background: '#0a0a0a', color: '#fff' }}>$5,000 - $15,000 / Month</option>
                          <option value="$15k+ / Month" style={{ background: '#0a0a0a', color: '#fff' }}>$15,000+ / Month</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-500">Directive / Message (NO URLs ALLOWED)</label>
                      <textarea
                        required
                        rows={4}
                        className={`${inputClasses} resize-none`}
                        placeholder="Detail your partnership objectives..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      />
                    </div>

                    <div className="pt-4 flex justify-end">
                      <button
                        type="submit"
                        disabled={!isFormValid || status === "loading"}
                        className="group relative px-8 py-4 bg-white text-black font-bold text-xs uppercase tracking-widest hover:bg-brand hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                      >
                        <span className="relative z-10 flex items-center gap-2">
                          {status === "loading" ? "Transmitting..." : "Submit Inquiry"}
                          <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                        <div className="absolute inset-0 bg-brand translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
